import * as React from 'react'

import { format, parseISO } from 'date-fns'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SingleDayPickerInput } from '@/components/ui/single-day-picker-input'
import { Textarea } from '@/components/ui/textarea'

import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { eventSchema, type TEventFormData } from '@/features/calendar/schemas'
import { getStoredEventColor, setStoredEventColor } from '@/features/calendar/utils/color-store'

import { useCreateEvent, useGetEventById, useUpdateEvent } from '@/utils/api/membership-service'
import type { CPDEventCreateDTO, CPDEventResponseDTO, CPDEventUpdateDTO } from '@/utils/api/membership-service/models'

const HOURS = Array.from({ length: 12 }, (_, index) => index + 1)
const MINUTES = [0, 15, 30, 45]
const PERIODS = ['AM', 'PM'] as const

type EventFormDialogProps = {
  trigger: React.ReactNode
  title: string
  description: string
  submitLabel: string
  initialValues: TEventFormData
  isSubmitting: boolean
  onSubmit: (values: TEventFormData) => Promise<void>
  onClose?: () => void
}

function EventFormDialog({
  trigger,
  title,
  description,
  submitLabel,
  initialValues,
  isSubmitting,
  onSubmit,
  onClose,
}: EventFormDialogProps) {
  const [open, setOpen] = React.useState(false)

  const form = useForm<TEventFormData>({
    resolver: zodResolver(eventSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  })

  const startDateValue = form.watch('startDate')
  const endDateValue = form.watch('endDate')

  React.useEffect(() => {
    if (open) {
      form.reset(initialValues)
    }
  }, [form, initialValues, open])

  React.useEffect(() => {
    if (!startDateValue || !endDateValue) return

    const start = stripTime(startDateValue)
    const end = stripTime(endDateValue)

    if (end.getTime() < start.getTime()) {
      form.setValue('endDate', start, { shouldValidate: true, shouldDirty: true })
    }
  }, [endDateValue, form, startDateValue])

  const deliveryMode = form.watch('deliveryMode')

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      form.reset(initialValues)
      onClose?.()
    }
  }

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await onSubmit(values)
      setOpen(false)
    } catch {
      // Leave the dialog open; the caller handles error feedback.
    }
  })

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel required htmlFor="event-title">
                    Title
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="event-title"
                      placeholder="Enter the event title"
                      data-invalid={fieldState.invalid}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="theme"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel htmlFor="event-theme">
                    Theme
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="event-theme"
                      placeholder="e.g. Digital Transformation in Microfinance"
                      data-invalid={fieldState.invalid}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-2 md:grid-cols-3">
              <FormField
                control={form.control}
                name="deliveryMode"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Delivery mode</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger data-invalid={fieldState.invalid}>
                          <SelectValue placeholder="Select how the session runs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_person">In person</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="variant"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Calendar colour</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger data-invalid={fieldState.invalid}>
                          <SelectValue placeholder="Choose a colour" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blue">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-blue-600" />
                              Blue
                            </div>
                          </SelectItem>
                          <SelectItem value="green">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-green-600" />
                              Green
                            </div>
                          </SelectItem>
                          <SelectItem value="red">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-red-600" />
                              Red
                            </div>
                          </SelectItem>
                          <SelectItem value="yellow">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-yellow-500" />
                              Yellow
                            </div>
                          </SelectItem>
                          <SelectItem value="purple">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-purple-600" />
                              Purple
                            </div>
                          </SelectItem>
                          <SelectItem value="orange">
                            <div className="flex items-center gap-2">
                              <div className="size-3.5 rounded-full bg-orange-500" />
                              Orange
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="points"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required htmlFor="event-points">
                      CPD points
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="event-points"
                        type="number"
                        min={0}
                        step={0.5}
                        data-invalid={fieldState.invalid}
                        value={field.value ?? ''}
                        onChange={event => {
                          const value = event.target.value
                          field.onChange(value === '' ? undefined : Number(value))
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {deliveryMode === 'in_person' && (
              <FormField
                control={form.control}
                name="location"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required htmlFor="event-location">
                      Venue / location
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="event-location"
                        placeholder="e.g. UIBFS Auditorium, Kampala"
                        value={field.value ?? ''}
                        data-invalid={fieldState.invalid}
                        onChange={event => field.onChange(event.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {deliveryMode === 'online' && (
              <FormField
                control={form.control}
                name="onlineLink"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required htmlFor="event-online-link">
                      Meeting link
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="event-online-link"
                        placeholder="https://"
                        value={field.value ?? ''}
                        data-invalid={fieldState.invalid}
                        onChange={event => field.onChange(event.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid gap-2 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required htmlFor="event-start-date">
                      Start date
                    </FormLabel>
                    <FormControl>
                      <SingleDayPickerInput
                        id="event-start-date"
                        placeholder="Choose date"
                        value={field.value}
                        startMonth={field.value ?? new Date()}
                        data-invalid={fieldState.invalid}
                        onSelect={date => field.onChange(date)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>Start time</FormLabel>
                    <FormControl>
                      <TimeDropdown
                        value={ensureTimeValue(field.value)}
                        onChange={field.onChange}
                        invalid={fieldState.invalid}
                        fieldName="start"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <FormField
                control={form.control}
                name="endDate"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required htmlFor="event-end-date">
                      End date
                    </FormLabel>
                    <FormControl>
                      <SingleDayPickerInput
                        id="event-end-date"
                        placeholder="Choose date"
                        value={field.value}
                        startMonth={startDateValue ?? new Date()}
                        fromDate={startDateValue ? stripTime(startDateValue) : undefined}
                        dayDisabled={startDateValue ? { before: stripTime(startDateValue) } : undefined}
                        data-invalid={fieldState.invalid}
                        onSelect={date => field.onChange(date)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel required>End time</FormLabel>
                    <FormControl>
                      <TimeDropdown
                        value={ensureTimeValue(field.value)}
                        onChange={field.onChange}
                        invalid={fieldState.invalid}
                        fieldName="end"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel htmlFor="event-description">
                    Description
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      id="event-description"
                      placeholder="Provide a short summary, objectives, and key takeaways."
                      value={field.value ?? ''}
                      data-invalid={fieldState.invalid}
                      onChange={event => field.onChange(event.target.value)}
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface CreateEventDialogProps {
  children: React.ReactNode
  startDate?: Date
  startTime?: { hour: number; minute: number }
}

export function CreateEventDialog({ children, startDate, startTime }: CreateEventDialogProps) {
  const queryClient = useQueryClient()
  const mutation = useCreateEvent()

  const defaultValues = React.useMemo(
    () => buildCreateDefaults(startDate, startTime),
    [startDate, startTime],
  )

  const handleSubmit = React.useCallback(
    async (values: TEventFormData) => {
      const payload = toEventDto(values)

      try {
        const response = await mutation.mutateAsync({ data: payload })
        await queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] })
        const createdId = extractEventIdFromEnvelope(response.data)
        if (typeof createdId === 'number') {
          setStoredEventColor(createdId, values.variant)
        }
        toast.success('Event created successfully.')
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Unable to create the event. Please try again.'))
        throw error
      }
    },
    [mutation, queryClient],
  )

  return (
    <EventFormDialog
      trigger={children}
      title="Add CPD event"
      description="Create a new CPD training, webinar, or networking session."
      submitLabel="Create event"
      initialValues={defaultValues}
      isSubmitting={mutation.isPending}
      onSubmit={handleSubmit}
      onClose={() => mutation.reset()}
    />
  )
}

interface EditEventDialogProps {
  children: React.ReactNode
  event: ICalendarEvent
}

export function EditEventDialog({ children, event }: EditEventDialogProps) {
  const queryClient = useQueryClient()
  const mutation = useUpdateEvent()

  const eventId = React.useMemo(() => preferredEventId(event), [event])
  const eventDetailsQuery = useGetEventById(eventId ?? 0, {
    query: {
      enabled: typeof eventId === 'number' && eventId > 0,
      staleTime: 2 * 60 * 1000,
    },
  })
  const eventDetails = eventDetailsQuery.data?.data

  const defaultValues = React.useMemo(
    () => buildEditDefaults(event, eventDetails),
    [event, eventDetails],
  )

  const handleSubmit = React.useCallback(
    async (values: TEventFormData) => {
      if (typeof eventId === 'undefined') {
        toast.error('We could not determine the event identifier for editing.')
        throw new Error('Missing event identifier')
      }

      const payload = toEventDto(values)

      try {
        await mutation.mutateAsync({ id: eventId, data: payload })
        await queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] })
        setStoredEventColor(eventId, values.variant)
        toast.success('Event updated successfully.')
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Unable to update the event. Please try again.'))
        throw error
      }
    },
    [eventId, mutation, queryClient],
  )

  return (
    <EventFormDialog
      trigger={children}
      title="Edit CPD event"
      description="Update the schedule, meeting link, or details for this session."
      submitLabel="Save changes"
      initialValues={defaultValues}
      isSubmitting={mutation.isPending}
      onSubmit={handleSubmit}
      onClose={() => mutation.reset()}
    />
  )
}

function buildCreateDefaults(startDate?: Date, startTime?: { hour: number; minute: number }): TEventFormData {
  const baseStart = startDate ? stripTime(startDate) : stripTime(new Date())
  const startHour = typeof startTime?.hour === 'number' ? startTime.hour : 9
  const startMinute = typeof startTime?.minute === 'number' ? startTime.minute : 0

  const startDateTime = new Date(baseStart)
  startDateTime.setHours(startHour, startMinute, 0, 0)

  const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000)

  return {
    title: '',
    theme: '',
    description: '',
    startDate: stripTime(startDateTime),
    startTime: toTimeParts(startDateTime),
    endDate: stripTime(endDateTime),
    endTime: toTimeParts(endDateTime),
    variant: 'blue',
    deliveryMode: 'in_person',
    location: '',
    onlineLink: '',
    points: 0,
  }
}

function buildEditDefaults(event: ICalendarEvent, details?: CPDEventResponseDTO): TEventFormData {
  const title = (details?.title ?? event.title ?? '').trim()
  const description = details?.description ?? event.description ?? ''
  const rawTheme = details?.theme ?? event.theme ?? ''
  const theme = typeof rawTheme === 'string' ? rawTheme.trim() : ''
  const start = safeParseDate(details?.startAt ?? event.startAt)
  const end = safeParseDate(details?.endAt ?? event.endAt)
  const isOnline = details?.isOnline ?? event.isOnline ?? false
  const venue = details?.venue ?? event.location ?? ''
  const onlineLink = details?.onlineLink ?? event.onlineLink ?? ''
  const points = details?.points ?? event.points ?? 0

  return {
    title,
    theme,
    description,
    startDate: stripTime(start),
    startTime: toTimeParts(start),
    endDate: stripTime(end),
    endTime: toTimeParts(end),
    variant: getStoredEventColor(preferredEventId(event)) ?? event.color ?? 'blue',
    deliveryMode: isOnline ? 'online' : 'in_person',
    location: isOnline ? '' : venue,
    onlineLink: isOnline ? onlineLink : '',
    points,
  }
}

function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function safeParseDate(value: string): Date {
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

function combineDateAndTime(date: Date, time: { hour: number; minute: number; period: 'AM' | 'PM' }): string {
  const combined = new Date(date)
  let hour = time.hour % 12
  if (time.period === 'PM') {
    hour += 12
  }
  if (time.period === 'AM' && time.hour === 12) {
    hour = 0
  }
  if (time.period === 'PM' && time.hour === 12) {
    hour = 12
  }
  combined.setHours(hour, time.minute, 0, 0)
  return format(combined, "yyyy-MM-dd'T'HH:mm:ss")
}

function toEventDto(values: TEventFormData): CPDEventCreateDTO | CPDEventUpdateDTO {
  const isOnline = values.deliveryMode === 'online'
  const startAt = combineDateAndTime(values.startDate, values.startTime)
  const endAt = combineDateAndTime(values.endDate, values.endTime)

  return {
    title: values.title.trim(),
    theme: values.theme?.trim() || undefined,
    description: values.description?.trim() || undefined,
    startAt,
    endAt,
    venue: !isOnline ? values.location?.trim() || undefined : undefined,
    isOnline,
    onlineLink: isOnline ? values.onlineLink?.trim() || undefined : undefined,
    points: typeof values.points === 'number' ? values.points : 0,
    facilitators: undefined,
    registrationDeadline: undefined,
    cost: undefined,
    certificateTemplate: undefined,
  }
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const maybeError = error as { message?: unknown; response?: unknown }

    if (maybeError.response && typeof maybeError.response === 'object') {
      const response = maybeError.response as { data?: unknown }
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>
        if (typeof data.message === 'string' && data.message.trim()) {
          return data.message
        }
        if (typeof data.error === 'string' && data.error.trim()) {
          return data.error
        }
      }
    }

    if (typeof maybeError.message === 'string' && maybeError.message.trim()) {
      return maybeError.message
    }
  }

  return fallback
}

function ensureTimeValue(value: TEventFormData['startTime'] | undefined): { hour: number; minute: number; period: 'AM' | 'PM' } {
  const rawHour = Number.isFinite(value?.hour) ? Number(value?.hour) : 9
  const normalizedHour = ((Math.round(rawHour) - 1 + 12) % 12) + 1
  const minute = Number.isFinite(value?.minute) ? Math.max(0, Math.min(59, Math.round(Number(value?.minute)))) : 0
  const period = value?.period === 'PM' ? 'PM' : 'AM'
  return { hour: normalizedHour, minute, period }
}

function TimeDropdown({
  value,
  onChange,
  invalid,
  fieldName,
}: {
  value: { hour: number; minute: number; period: 'AM' | 'PM' }
  onChange: (next: { hour: number; minute: number; period: 'AM' | 'PM' }) => void
  invalid?: boolean
  fieldName: 'start' | 'end'
}) {
  const hourValue = pad(value.hour)
  const minuteValue = pad(value.minute)
  const minuteOptions = React.useMemo(() => {
    if (MINUTES.includes(value.minute)) {
      return MINUTES
    }
    return Array.from(new Set([...MINUTES, value.minute])).sort((a, b) => a - b)
  }, [value.minute])

  return (
    <div className="flex items-center gap-2">
      <Select value={hourValue} onValueChange={nextHour => onChange({ hour: Number(nextHour), minute: value.minute, period: value.period })}>
        <SelectTrigger data-invalid={invalid} aria-label={`${fieldName} hour`}>
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent className="max-h-60 overflow-auto">
          {HOURS.map(hour => {
            const formatted = pad(hour)
            return (
              <SelectItem key={`hour-${hour}`} value={formatted}>
                {formatted}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground text-sm">:</span>

      <Select
        value={minuteValue}
        onValueChange={nextMinute => onChange({ hour: value.hour, minute: Number(nextMinute), period: value.period })}
      >
        <SelectTrigger data-invalid={invalid} aria-label={`${fieldName} minute`}>
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent className="max-h-60 overflow-auto">
          {minuteOptions.map(minute => {
            const formatted = pad(minute)
            return (
              <SelectItem key={`minute-${minute}`} value={formatted}>
                {formatted}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      <Select
        value={value.period}
        onValueChange={nextPeriod => onChange({ hour: value.hour, minute: value.minute, period: nextPeriod as 'AM' | 'PM' })}
      >
        <SelectTrigger data-invalid={invalid} aria-label={`${fieldName} period`}>
          <SelectValue placeholder="AM" />
        </SelectTrigger>
        <SelectContent>
          {PERIODS.map(period => (
            <SelectItem key={`period-${period}`} value={period}>
              {period}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function toTimeParts(date: Date): { hour: number; minute: number; period: 'AM' | 'PM' } {
  let hours = date.getHours()
  const minutes = date.getMinutes()
  const period: 'AM' | 'PM' = hours >= 12 ? 'PM' : 'AM'
  const normalizedHour = hours % 12 === 0 ? 12 : hours % 12
  return {
    hour: normalizedHour,
    minute: minutes,
    period,
  }
}

function pad(value: number): string {
  return value.toString().padStart(2, '0')
}

function extractEventIdFromEnvelope(payload: unknown): number | undefined {
  if (payload == null) return undefined

  if (typeof payload === 'number' && Number.isFinite(payload)) {
    return payload
  }

  if (typeof payload === 'string') {
    const parsed = Number(payload)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    const candidateKeys = ['id', 'eventId', 'cpdEventId']

    for (const key of candidateKeys) {
      if (key in record) {
        const value = extractEventIdFromEnvelope(record[key])
        if (typeof value === 'number') {
          return value
        }
      }
    }

    if ('data' in record) {
      const nested = extractEventIdFromEnvelope(record.data)
      if (typeof nested === 'number') {
        return nested
      }
    }
  }

  return undefined
}

function preferredEventId(event: ICalendarEvent): number | undefined {
  if (typeof event.sourceId === 'number' && Number.isFinite(event.sourceId)) {
    return event.sourceId
  }
  const parsed = Number(event.id)
  return Number.isFinite(parsed) ? parsed : undefined
}
